import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { devLog } from "@/lib/log";

const LOGO_URL = "/logo-transparent.png";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!token) return setError("Reset link is missing a token.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      toast("Password updated. You're signed in.", { duration: 2400 });
      // After reset, fetch current user to choose the right destination.
      try {
        const me = await api.get("/auth/me");
        if (me.data.role === "couple") navigate("/my-favourites");
        else if (me.data.role === "vendor") navigate("/");
        else navigate("/");
      } catch (e) {
        devLog("ld_reset post-success /auth/me failed:", e?.message);
        navigate("/");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't reset password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="reset-password-page"
      className="min-h-[100dvh] flex flex-col"
      style={{ backgroundColor: "var(--ld-ivory)" }}
    >
      <header className="flex items-center justify-between gap-6 px-6 md:px-12 pt-5 md:pt-7">
        <Link
          to="/signin"
          className="inline-flex items-center gap-2 text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]"
        >
          <ArrowLeft size={14} strokeWidth={1.6} />
          <span className="font-accent text-[10px] tracking-[0.25em]">Back</span>
        </Link>
        <span className="font-accent text-[11px] md:text-xs text-[var(--ld-gold-deep)]">
          Luxé · Directory
        </span>
      </header>

      <section className="flex-1 flex flex-col items-center justify-center px-6 md:px-12 py-16">
        <img src={LOGO_URL} alt="Luxé" className="w-[150px] sm:w-[170px] h-auto" />
        <span className="mt-6 font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
          Recovery
        </span>
        <h1 className="font-serif-display mt-3 text-3xl sm:text-4xl text-[var(--ld-ink)]">
          Choose a new password.
        </h1>

        <div className="mt-10 w-full max-w-md space-y-5">
          <label className="block relative">
            <Lock
              size={14}
              strokeWidth={1.6}
              className="absolute left-0 top-1/2 -translate-y-1/2 text-[var(--ld-ink-muted)] pointer-events-none"
            />
            <input
              data-testid="reset-password-input"
              type={showPwd ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password (min 8 chars)"
              autoFocus
              className="w-full bg-transparent border-b border-[var(--ld-ink)]/35 pl-6 pr-9 py-3 text-[15px] text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)]"
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              aria-label={showPwd ? "Hide password" : "Show password"}
              className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]"
            >
              {showPwd ? <EyeOff size={14} strokeWidth={1.6} /> : <Eye size={14} strokeWidth={1.6} />}
            </button>
          </label>
          <label className="block relative">
            <Lock
              size={14}
              strokeWidth={1.6}
              className="absolute left-0 top-1/2 -translate-y-1/2 text-[var(--ld-ink-muted)] pointer-events-none"
            />
            <input
              data-testid="reset-password-confirm"
              type={showPwd ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm password"
              className="w-full bg-transparent border-b border-[var(--ld-ink)]/35 pl-6 pr-9 py-3 text-[15px] text-[var(--ld-ink)] focus:outline-none focus:border-[var(--ld-gold-deep)]"
            />
          </label>
          <button
            data-testid="reset-password-submit"
            onClick={submit}
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 text-[11px] tracking-[0.22em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] px-5 py-3.5 hover:opacity-90 disabled:opacity-60"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Update password
          </button>
          {error && (
            <p data-testid="reset-error" className="text-[12px] text-rose-600 text-center">
              {error}
            </p>
          )}
        </div>
      </section>
    </main>
  );
};

export default ResetPassword;
